// ====================== BLOCK 1/2 ======================
// src/pages/admin/PaymentRequestView.tsx
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { getToken, openAuthenticatedUrl } from "../../lib/auth";

/** 서버에서 올 수 있는 업로드 항목의 다양한 형태를 수용 */
type RawUpload =
  | string
  | {
      id?: number;
      name?: string;
      filename?: string;
      saved_name?: string;
      original_name?: string;
      url?: string;
      preview_url?: string;
      download_url?: string;
      doc_type?: string | null;
      status?: string | null;
      created_at?: string;
    };

/** 표준 + 레거시 호환 필드를 모두 가진 Payment */
type Payment = {
  id: number;
  status?: string | null;
  method?: string | null;
  category?: string | null; // ✅ rent / goods … 코드만
  amount?: number | null;
  fee_amount?: number | null;
  vat_amount?: number | null;

  /** ✅ 관리자 화면 "신청인 정보"에 표시될 값(송금자명 영향 금지) */
  applicant_name?: string | null;
  applicant_phone?: string | null;
  email?: string | null;

  files?: RawUpload[];

  counterparty_name?: string | null;
  counterparty_bank_name?: string | null;
  counterparty_account?: string | null;
  bank_code?: string | null;
  transfer_date?: string | null;
  approval_reserved_at?: string | null;

  approval_status?: string | null;
  reject_reason?: string | null;

  // legacy
  state?: string | null;
  title?: string | null;
  payment_name?: string | null;
  total_amount?: number | null;
  price?: number | null;
  user_name?: string | null;
  phone?: string | null;
  customer_name?: string | null;
  meta?: any;

  // ✅ 사용자가 결제등록 화면에서 입력한 기타 메모
  extra_note?: string | null;

  // ✅ 송금자명(이체 시 표시되는 이름)
  remitter_name?: string | null;

  // ✅ 등기부등본용 주소 (meta.registry_address 에서 파생)
  registry_addr1?: string | null;
  registry_addr2?: string | null;
  registry_zonecode?: string | null;
  registry_address?: {
    addr1?: string;
    addr2?: string;
    zonecode?: string;
    [k: string]: any;
  } | null;
  agreement_logs?: AgreementLog[];
};

type AgreementLog = {
  id?: number;
  agreement_type?: string | null;
  category?: string | null;
  session_key?: string | null;
  notice_version?: string | null;
  notice_text?: string | null;
  agreed?: boolean | null;
  agreed_at?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
};

type UiFile = {
  name: string;
  previewUrl: string;
  downloadUrl: string;
  docType?: string | null;
  status?: string | null;
  createdAt?: string;
};

type SplitSummary = {
  split_count: number;
  amount_sum: number;
  fee_sum: number;
  vat_sum: number;
  total_sum: number;
};

type SplitItem = {
  id: number;
  split_no: number;
  split_label: string;
  approval_status?: string | null;
  status?: string | null;
  amount: number;
  fee_amount: number;
  vat_amount: number;
  total_amount: number;
  created_at?: string | null;
};

const PAY_TYPE_KO: Record<string, string> = {
  rent: "임대료/월세",
  goods: "물품대금",
  salary: "급여",
  maintenance: "관리비",
  mgmt_fee: "관리비",
  rental: "렌탈료",

  // ✅ 추가: 주류페이 카테고리
  drink: "주류/음료",
};

function formatPayType(v?: string | null) {
  if (!v) return "-";
  const key = String(v).toLowerCase();
  return PAY_TYPE_KO[key] || v;
}

function optionalMoney(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
}

const API_BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/$/, "");
const withBase = (p: string) => (API_BASE ? `${API_BASE}${p}` : p);

// ✅ 링크(미리보기/다운로드)는 브라우저가 직접 여는 URL이므로 /api 유지
function buildAuthQuery() {
  if (typeof window === "undefined") return "";
  const adminToken = localStorage.getItem("xAdminToken") || "";
  const jwt = getToken();
  const qs = new URLSearchParams();
  if (adminToken) qs.set("admin_token", adminToken);
  if (jwt) qs.set("token", jwt);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

function buildPreviewUrl(file: RawUpload) {
  const tail = buildAuthQuery();
  if (typeof file === "string") {
    return withBase(`/api/uploads/preview/${encodeURIComponent(file)}${tail}`);
  }
  const id = (file as any)?.id;
  if (id) return withBase(`/api/uploads/preview-id/${encodeURIComponent(id)}${tail}`);
  const key =
    (file as any)?.filename ||
    (file as any)?.saved_name ||
    (file as any)?.original_name ||
    (file as any)?.name ||
    "";
  return key ? withBase(`/api/uploads/preview/${encodeURIComponent(key)}${tail}`) : "#";
}

function buildDownloadUrl(file: RawUpload) {
  const tail = buildAuthQuery();
  if (typeof file === "string") {
    return withBase(`/api/uploads/download/${encodeURIComponent(file)}${tail}`);
  }
  const id = (file as any)?.id;
  if (id) return withBase(`/api/uploads/download-id/${encodeURIComponent(id)}${tail}`);
  const key =
    (file as any)?.filename ||
    (file as any)?.saved_name ||
    (file as any)?.original_name ||
    (file as any)?.name ||
    "";
  return key ? withBase(`/api/uploads/download/${encodeURIComponent(key)}${tail}`) : "#";
}

/**
 * ✅ doc_type 한글 매핑
 * - 매핑 없는 코드는 "표시 안 함" 정책
 */
const DOC_TYPE_KO: Record<string, string> = {
  // 임대
  rent_contract: "임대차계약서",
  rent_history: "임차료 이체내역",

  // 관리비
  mgmt_receipt: "관리비 영수증",
  mgmt_fee: "관리비 영수증",
  mgmt_fee_maintenance: "관리비 영수증",
  mgmtfee_maintenance: "관리비 영수증",
  maintenance_receipt: "관리비 영수증",

  // 공통
  id_card: "신분증",
  business_license: "사업자등록증",
  biz_reg: "사업자등록증",
  tax_invoice: "전자세금계산서",
  estimate_or_statement: "견적서/거래명세서",

  // 급여
  payroll_statement: "급여명세서",
  social_insurance_list: "사대보험가입자명부",
  withholding_receipt: "원천징수영수증",
  salary_transfer_history: "급여 이체내역",

  // 렌탈
  rental_contract: "렌탈계약서",
  rental_payment_history: "렌탈료 납부내역",

  // 사업자 관련
  my_biz_reg: "본인 사업자등록증",
  counterparty_biz_reg: "본인 사업자등록증",
  counterparty_bank_copy: "상대방 통장사본",
};

const FILE_STATUS_KO: Record<string, string> = {
  pending: "대기",
  approved: "승인",
  rejected: "거절",
};

const BANK_NAME_BY_CODE: Record<string, string> = {
  "011": "NH농협",
  "004": "국민",
  "020": "우리",
  "088": "신한",
  "081": "하나",
  "090": "카카오뱅크",
  "089": "케이뱅크",
  "071": "우체국",
  "003": "기업",
  "023": "SC제일",
  "027": "한국씨티",
};

const APPROVAL_OPTIONS = [
  "승인대기",
  "결제대기",
  "결제완료",
  "송금완료",
  "요청철회/거절",
];

function mapApprovalCodeToLabel(raw?: string | null): string {
  if (!raw) return "승인대기";
  const v = String(raw).toLowerCase();
  if (APPROVAL_OPTIONS.includes(raw)) return raw;

  if (["pending", "request", "wait", "waiting", "requested"].includes(v))
    return "승인대기";

  if (
    ["approved", "ready", "waiting_for_payment", "pay_wait", "pay_ready"].includes(
      v
    )
  )
    return "결제대기";

  if (["paid", "complete", "completed", "done", "success"].includes(v))
    return "결제완료";

  if (["remitted", "sent", "transfer_done", "remit_complete"].includes(v))
    return "송금완료";

  if (["rejected", "denied", "canceled", "cancelled", "withdrawn"].includes(v))
    return "요청철회/거절";

  return "승인대기";
}

function toDateTimeLocalValue(raw?: string | null): string {
  if (!raw) return "";
  const v = String(raw).trim();
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return v;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T09:00`;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function toIsoOrNull(localDateTime: string): string | null {
  if (!localDateTime) return null;
  const d = new Date(localDateTime);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function formatDateTimeKST(raw?: string | null): string {
  if (!raw) return "-";
  const d = new Date(raw);
  if (!Number.isNaN(d.getTime())) {
    return new Intl.DateTimeFormat("ko-KR", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
  }
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return raw.replace("T", " ");
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return String(raw);
}
function extractRawUploads(data: any): RawUpload[] {
  if (!data) return [];
  if (Array.isArray(data)) return data as RawUpload[];
  if (Array.isArray(data.items)) return data.items as RawUpload[];
  if (Array.isArray(data.uploads)) return data.uploads as RawUpload[];
  if (Array.isArray(data.files)) return data.files as RawUpload[];
  if (Array.isArray(data.attachments)) return data.attachments as RawUpload[];
  return [];
}

/** ✅ 신청인은 송금자명/거래상대 정보와 분리 */
function pickApplicantName(payment: any, applicantObj?: any): string | null {
  return (
    payment?.applicant_name ??
    payment?.applicantName ??
    payment?.user_name ??
    payment?.userName ??
    payment?.requested_by_name ??
    payment?.requestedByName ??
    applicantObj?.name ??
    applicantObj?.full_name ??
    applicantObj?.fullName ??
    payment?.customer_name ??
    payment?.customerName ??
    payment?.meta?.applicant_name ??
    payment?.meta?.customer_name ??
    null
  );
}

function pickApplicantPhone(payment: any, applicantObj?: any): string | null {
  return (
    payment?.applicant_phone ??
    payment?.applicantPhone ??
    payment?.user_phone ??
    payment?.userPhone ??
    payment?.phone ??
    applicantObj?.phone ??
    applicantObj?.mobile ??
    payment?.customer_phone ??
    payment?.customerPhone ??
    payment?.meta?.applicant_phone ??
    payment?.meta?.customer_phone ??
    null
  );
}

function pickApplicantEmail(payment: any, applicantObj?: any): string | null {
  return (
    payment?.applicant_email ??
    payment?.applicantEmail ??
    payment?.user_email ??
    payment?.userEmail ??
    payment?.email ??
    applicantObj?.email ??
    payment?.customer_email ??
    payment?.customerEmail ??
    payment?.meta?.applicant_email ??
    payment?.meta?.customer_email ??
    null
  );
}

export default function AdminPaymentRequestView() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();

  const [item, setItem] = useState<Payment | null>(null);
  const [files, setFiles] = useState<UiFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [splitSummary, setSplitSummary] = useState<SplitSummary | null>(null);
  const [splitItems, setSplitItems] = useState<SplitItem[]>([]);

  const [approvalStatus, setApprovalStatus] = useState<string>("승인대기");
  const [rejectReason, setRejectReason] = useState<string>("");
  const [savingApproval, setSavingApproval] = useState(false);
  const [reserveAtInput, setReserveAtInput] = useState<string>("");
  const [reservedAt, setReservedAt] = useState<string | null>(null);
  const [savingReserve, setSavingReserve] = useState(false);

  // ✅ 카테고리 코드를 뽑기 위한 헬퍼 (PAY_TYPE_KO 에 존재하는 코드만 인정)
  const pickCategoryCode = (...vals: Array<string | null | undefined>) => {
    for (const v of vals) {
      if (!v) continue;
      const key = String(v).toLowerCase();
      if (PAY_TYPE_KO[key]) return key;
    }
    return null;
  };

  function normalize(data: any): {
    payment: Payment;
    uploads: RawUpload[];
    splitSummary: SplitSummary | null;
    splitItems: SplitItem[];
  } {
    const payment: any = data?.payment ?? data?.item ?? data ?? { id: 0 };
    const applicant: any = data?.applicant ?? data?.user ?? data?.customer ?? {};

    const rawMeta = payment.meta ?? {};
    const meta: any =
      typeof rawMeta === "string"
        ? (() => {
            try {
              return JSON.parse(rawMeta);
            } catch {
              return {};
            }
          })()
        : rawMeta && typeof rawMeta === "object" && !Array.isArray(rawMeta)
        ? rawMeta
        : {};

    // ⚠ payment.category 에 예금주가 들어온 적이 있어서, 카테고리는 여기서만 계산
    let categoryCode: string | null = pickCategoryCode(
      payment.payment_type,
      payment.pay_category,
      payment.contract_type,
      payment.type,
      typeof meta.category === "string" ? meta.category : null,
      typeof meta.category_slug === "string" ? meta.category_slug : null
    );

    if (!categoryCode && typeof payment.method === "string") {
      categoryCode = pickCategoryCode(payment.method);
    }

    const methodRaw: string | null =
      payment.title ?? payment.payment_name ?? payment.method_text ?? null;

    // ✅ 신청인 정보 (송금자명 영향 차단
    const applicantName: string | null = pickApplicantName(payment, applicant);
    const applicantPhone: string | null = pickApplicantPhone(payment, applicant);
    const applicantEmail: string | null = pickApplicantEmail(payment, applicant);

    const bankCode: string | null =
      meta.bank_code ?? meta.bankCode ?? payment.bank_code ?? null;

    const counterpartyAccount: string | null =
      meta.counterparty_account ??
      meta.account_no ??
      meta.account ??
      payment.counterparty_account ??
      null;

    const bankNameFromCode =
      bankCode && BANK_NAME_BY_CODE[bankCode]
        ? BANK_NAME_BY_CODE[bankCode]
        : bankCode || null;

    const counterpartyBankName: string | null =
      meta.counterparty_bank_name ??
      meta.bank_name ??
      payment.counterparty_bank_name ??
      bankNameFromCode ??
      null;

    // ✅ 거래상대(예금주)는 신청인과 분리
    const counterpartyName: string | null =
      meta.counterparty_name ??
      meta.receiver_name ??
      meta.account_holder ??
      meta.holder_name ??
      payment.counterparty_name ??
      null;

    const transferDate: string | null =
      meta.transfer_date ?? meta.remit_date ?? payment.transfer_date ?? null;
    const approvalReservedAt: string | null =
      payment.approval_reserved_at ??
      payment.approval_schedule_at ??
      meta.approval_reserved_at ??
      meta.approval_schedule_at ??
      meta.approval_reserve_at ??
      null;

    // ✅ 송금자명
    const remitterName: string | null =
      meta.remitter_name ??
      meta.sender_name ??
      meta.depositor_name ??
      meta.deposit_name ??
      meta.transfer_sender_name ??
      meta.transfer_sender ??
      meta.transferName ??
      meta.remit_name ??
      null;

    // ✅ 등기부 주소
    const regObj: any =
      meta.registry_address || meta.registryAddr || meta.registry || null;

    const registry_addr1: string | null =
      (regObj && (regObj.addr1 || regObj.address1 || regObj.address)) || null;
    const registry_addr2: string | null =
      (regObj && (regObj.addr2 || regObj.address2 || regObj.detail)) || null;
    const registry_zonecode: string | null =
      (regObj && (regObj.zonecode || regObj.zipcode || regObj.postcode)) || null;

    // ✅ 기타 메모(결제등록 화면 "기타내용")
    let extraNote: string | null = null;

    if (typeof meta.extra_note === "string" && meta.extra_note.trim().length > 0) {
      extraNote = meta.extra_note;
    } else if (
      typeof meta.extraNote === "string" &&
      meta.extraNote.trim().length > 0
    ) {
      extraNote = meta.extraNote;
    } else if (typeof meta.memo === "string" && meta.memo.trim().length > 0) {
      extraNote = meta.memo;
    } else if (typeof meta.note === "string" && meta.note.trim().length > 0) {
      extraNote = meta.note;
    } else if (
      typeof payment.extra_note === "string" &&
      payment.extra_note.trim().length > 0
    ) {
      extraNote = payment.extra_note;
    } else if (
      typeof payment.extraNote === "string" &&
      payment.extraNote.trim().length > 0
    ) {
      extraNote = payment.extraNote;
    } else {
      extraNote = null;
    }

    const merged: Payment = {
      id: Number(payment.id),
      status: payment.status ?? payment.state ?? null,
      method: methodRaw ?? null,
      category: categoryCode, // ✅ rent/goods/drink… 코드만

      amount: optionalMoney(payment.amount) ?? optionalMoney(payment.total_amount) ?? optionalMoney(payment.price),
      fee_amount: optionalMoney(payment.fee_amount),
      vat_amount: optionalMoney(payment.vat_amount),
      total_amount: optionalMoney(payment.total_amount),

      applicant_name: applicantName,
      applicant_phone: applicantPhone,
      email: applicantEmail,

      counterparty_name: counterpartyName,
      counterparty_bank_name: counterpartyBankName,
      counterparty_account: counterpartyAccount,

      bank_code: bankCode,
      transfer_date: transferDate,
      approval_reserved_at: approvalReservedAt,

      approval_status: payment.approval_status ?? null,
      reject_reason: payment.reject_reason ?? null,

      customer_name: payment.customer_name ?? null,
      meta,

      files:
        data?.uploads ??
        data?.files ??
        data?.attachments ??
        payment.files ??
        [],

      registry_addr1,
      registry_addr2,
      registry_zonecode,
      registry_address: regObj || null,

      remitter_name: remitterName,

      extra_note: extraNote,
      agreement_logs: Array.isArray(data?.agreement_logs)
        ? data.agreement_logs
        : Array.isArray(payment.agreement_logs)
        ? payment.agreement_logs
        : [],
    };

    const rawSummary = data?.split_summary || null;
    const normalizedSplitSummary: SplitSummary | null = rawSummary
      ? {
          split_count: Number(rawSummary.split_count || 0),
          amount_sum: Number(rawSummary.amount_sum || 0),
          fee_sum: Number(rawSummary.fee_sum || 0),
          vat_sum: Number(rawSummary.vat_sum || 0),
          total_sum: Number(rawSummary.total_sum || 0),
        }
      : null;

    const normalizedSplitItems: SplitItem[] = Array.isArray(data?.split_items)
      ? data.split_items.map((r: any, idx: number) => ({
          id: Number(r.id || 0),
          split_no: Number(r.split_no || idx + 1),
          split_label: String(r.split_label || ((idx + 1) + "/" + Math.max(1, data.split_items.length))),
          approval_status: r.approval_status || null,
          status: r.status || null,
          amount: Number(r.amount || 0),
          fee_amount: Number(r.fee_amount || 0),
          vat_amount: Number(r.vat_amount || 0),
          total_amount: Number(r.total_amount || 0),
          created_at: r.created_at || null,
        }))
      : [];

    return {
      payment: merged,
      uploads: Array.isArray(merged.files) ? merged.files : [],
      splitSummary: normalizedSplitSummary,
      splitItems: normalizedSplitItems,
    };
  }

  /** ✅ doc_type / status 표시 정책 */
  function normalizeUploads(raw: RawUpload[]): UiFile[] {
    if (!Array.isArray(raw)) return [];
    const out: UiFile[] = [];

    for (const f of raw) {
      if (typeof f === "string") {
        out.push({
          name: f,
          previewUrl: buildPreviewUrl(f),
          downloadUrl: buildDownloadUrl(f),
        });
        continue;
      }
      if (!f || typeof f !== "object") continue;

      const fileForUrl =
        f.filename || f.saved_name || f.original_name || f.name || "";

      const displayName =
        f.original_name || f.name || f.filename || f.saved_name || "첨부파일";

      const previewUrl = buildPreviewUrl(f);
      const downloadUrl = buildDownloadUrl(f);

      const rawDoc = f.doc_type ? String(f.doc_type).trim() : "";
      const rawStatus = f.status ? String(f.status).trim() : "";

      // ✅ doc_type은 "매핑된 한글만" 표시
      const docTypeLabel = rawDoc ? DOC_TYPE_KO[rawDoc] ?? null : null;

      // ✅ status는 한글 매핑 후, '대기'는 숨김
      const statusLabel = rawStatus
        ? FILE_STATUS_KO[rawStatus] || rawStatus
        : null;
      const safeStatus = statusLabel === "대기" ? null : statusLabel;

      out.push({
        name: displayName,
        previewUrl,
        downloadUrl,
        docType: docTypeLabel,
        status: safeStatus,
        createdAt: f.created_at,
      });
    }

    return out;
  }

  const fetchDetail = async () => {
    if (!id) return;
    setLoading(true);
    try {
      // ✅ 핵심 수정: axios(baseURL="/api") 이므로 여기서는 "/api" 제거
      let res;
      try {
        res = await api.get(`/admin/payment-requests/${id}`);
      } catch {
        res = await api.get(`/admin/payments/${id}`);
      }

      const { payment, uploads, splitSummary, splitItems } = normalize(res.data ?? {});
      if (!payment?.id) throw new Error("invalid response");

      setItem(payment);
      setSplitSummary(splitSummary);
      setSplitItems(splitItems);

      const initialApproval = mapApprovalCodeToLabel(
        payment.approval_status ?? payment.status ?? null
      );
      setApprovalStatus(initialApproval);
      setRejectReason(payment.reject_reason || "");
      setReservedAt(payment.approval_reserved_at ?? null);
      setReserveAtInput(toDateTimeLocalValue(payment.approval_reserved_at));

      let fileList: UiFile[] = [];

      try {
        const uploadRes = await api
          .get(`/admin/payments/${id}/uploads`)
          .catch(async () => {
            return await api.get(`/admin/payment-requests/${id}/uploads`);
          });

        const raw = extractRawUploads(uploadRes.data);
        fileList = normalizeUploads(raw);
      } catch (uploadError) {
        console.warn("별도 업로드 조회 실패, 기본 응답 사용:", uploadError);
        fileList = normalizeUploads(uploads);
      }

      setFiles(fileList);
    } catch (e) {
      console.error(e);
      setSplitSummary(null);
      setSplitItems([]);
      alert("상세 정보를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveApproval = async () => {
    if (!id) return;

    if (approvalStatus === "요청철회/거절" && !rejectReason.trim()) {
      const go = window.confirm("거절 사유 없이 저장하시겠습니까?");
      if (!go) return;
    }

    setSavingApproval(true);
    try {
      const payload = {
        approval_status: approvalStatus,
        reject_reason:
          approvalStatus === "요청철회/거절" ? rejectReason.trim() || null : null,
      };

      // ✅ 핵심 수정: axios(baseURL="/api") 이므로 "/api" 제거
      try {
        await api.patch(`/admin/payment-requests/${id}/approval`, payload);
      } catch {
        await api.patch(`/admin/payments/${id}/approval`, payload);
      }

      alert("결제 승인 상태가 저장되었습니다.");
      fetchDetail();
    } catch (e) {
      console.error(e);
      alert("승인 상태 저장 중 오류가 발생했습니다.");
    } finally {
      setSavingApproval(false);
    }
  };

  const upsertApprovalSchedule = async (scheduleAtIso: string | null) => {
    if (!id) return false;
    const payload = {
      scheduled_at: scheduleAtIso,
      approval_reserved_at: scheduleAtIso,
      approval_schedule_at: scheduleAtIso,
      timezone: "Asia/Seoul",
    };

    const tries: Array<{
      fn: "post" | "patch" | "delete";
      path: string;
      body?: any;
    }> = scheduleAtIso
      ? [
          { fn: "post", path: `/admin/payment-requests/${id}/approval-schedule`, body: payload },
          { fn: "post", path: `/admin/payments/${id}/approval-schedule`, body: payload },
          { fn: "patch", path: `/admin/payment-requests/${id}/schedule-approval`, body: payload },
          { fn: "patch", path: `/admin/payments/${id}/schedule-approval`, body: payload },
        ]
      : [
          { fn: "delete", path: `/admin/payment-requests/${id}/approval-schedule` },
          { fn: "delete", path: `/admin/payments/${id}/approval-schedule` },
          { fn: "patch", path: `/admin/payment-requests/${id}/schedule-approval`, body: payload },
          { fn: "patch", path: `/admin/payments/${id}/schedule-approval`, body: payload },
        ];

    for (const t of tries) {
      try {
        if (t.fn === "post") {
          await api.post(t.path, t.body ?? {});
        } else if (t.fn === "patch") {
          await api.patch(t.path, t.body ?? {});
        } else {
          await api.delete(t.path);
        }
        return true;
      } catch {
        // 다음 후보 API 시도
      }
    }
    return false;
  };

  const saveReserve = async () => {
    if (!id) return;
    if (!reserveAtInput) {
      alert("예약할 일시를 선택해주세요.");
      return;
    }
    const scheduleAtIso = toIsoOrNull(reserveAtInput);
    if (!scheduleAtIso) {
      alert("예약 일시 형식이 올바르지 않습니다.");
      return;
    }

    setSavingReserve(true);
    try {
      const ok = await upsertApprovalSchedule(scheduleAtIso);
      if (!ok) {
        alert("예약 API가 아직 연결되지 않았습니다. 서버에 approval-schedule 엔드포인트가 필요합니다.");
        return;
      }
      alert("결제대기 예약이 저장되었습니다.");
      setReservedAt(scheduleAtIso);
      fetchDetail();
    } catch (e) {
      console.error(e);
      alert("예약 저장 중 오류가 발생했습니다.");
    } finally {
      setSavingReserve(false);
    }
  };

  const cancelReserve = async () => {
    if (!id) return;
    setSavingReserve(true);
    try {
      const ok = await upsertApprovalSchedule(null);
      if (!ok) {
        alert("예약 취소 API가 아직 연결되지 않았습니다. 서버에 approval-schedule 엔드포인트가 필요합니다.");
        return;
      }
      alert("결제대기 예약이 취소되었습니다.");
      setReservedAt(null);
      setReserveAtInput("");
      fetchDetail();
    } catch (e) {
      console.error(e);
      alert("예약 취소 중 오류가 발생했습니다.");
    } finally {
      setSavingReserve(false);
    }
  };
  // ===== 금액/수수료 표시 =====
  const amount = splitSummary?.amount_sum ?? item?.amount ?? null;
  const fee = splitSummary?.fee_sum ?? item?.fee_amount ?? (amount != null ? Math.ceil(amount * 0.037) : 0);
  const vat = splitSummary?.vat_sum ?? item?.vat_amount ?? (fee ? Math.ceil(fee * 0.1) : 0);
  const total = splitSummary?.total_sum ?? item?.total_amount ?? (amount != null ? amount + fee + vat : null);

  const counterpartyName = item?.counterparty_name || "-";
  const counterpartyBankName =
    item?.counterparty_bank_name ||
    (item?.bank_code ? BANK_NAME_BY_CODE[item.bank_code] || item.bank_code : "") ||
    "-";
  const counterpartyAccount = item?.counterparty_account || "-";

  // ✅ 송금자명 (표시용)
  const remitterDisplay =
    item?.remitter_name ||
    (item?.meta &&
      typeof item.meta === "object" &&
      (item.meta.remitter_name ||
        item.meta.sender_name ||
        item.meta.depositor_name ||
        item.meta.deposit_name ||
        item.meta.transfer_sender_name ||
        item.meta.transfer_sender ||
        item.meta.transferName ||
        item.meta.remit_name)) ||
    "-";

  // ✅ 등기부등본 발급용 주소 표시용 값
  const regObj = (item?.registry_address || {}) as any;
  const regAddr1 =
    item?.registry_addr1 || regObj.addr1 || regObj.address1 || regObj.address;
  const regAddr2 =
    item?.registry_addr2 || regObj.addr2 || regObj.address2 || regObj.detail;
  const regZone =
    item?.registry_zonecode || regObj.zonecode || regObj.zipcode || regObj.postcode;

  // ✅ "임대료/월세(rent)" 일 때만 주소 섹션 노출
  const showRegistrySection = item?.category === "rent";
  const vatAgreementLog = Array.isArray(item?.agreement_logs)
    ? item?.agreement_logs.find((log) =>
        String(log?.agreement_type || "").includes("vat_double_refund_notice")
      ) || null
    : null;

  // ✅ 명세서 버튼 활성 조건
  const canStatement = useMemo(() => approvalStatus === "송금완료", [approvalStatus]);

  const openStatement = () => {
    if (!id) return;
    const token = getToken();
    const basePath = `/api/admin/payments/${id}/statement`;

    const url = token
      ? withBase(`${basePath}?token=${encodeURIComponent(token)}`)
      : withBase(basePath);

    void openAuthenticatedUrl(url);
  };
// ====================== BLOCK 2/2 ======================

  return (
    <div className="jp-admin-request-detail space-y-4">
      {/* ✅ 페이지 제목 + (모바일) 저장 버튼 + 목록 버튼 */}
      <div className="jp-admin-page-heading jp-admin-detail-heading">
        <h2 className="min-w-0 text-lg font-semibold text-slate-900">
          결제 요청 #{id}
        </h2>

        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => nav(-1)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            목록
          </button>

          <button
            type="button"
            onClick={saveApproval}
            disabled={savingApproval}
            className="rounded-lg bg-sky-600 px-4 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-sky-500 disabled:opacity-60"
          >
            {savingApproval ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>

      {/* 기본 정보 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-700">기본 정보</h3>
          <button
            type="button"
            onClick={openStatement}
            disabled={!canStatement}
            title={canStatement ? "거래명세서 보기" : "송금완료 상태에서만 발행됩니다."}
            className={[
              "rounded-lg px-3 py-1.5 text-xs font-semibold",
              canStatement
                ? "bg-emerald-600 text-white hover:brightness-110"
                : "bg-slate-100 text-slate-400 cursor-not-allowed",
            ].join(" ")}
          >
            거래명세서
          </button>
        </div>

        <div className="jp-admin-request-overview">
          <div className="space-y-1">
            <div className="text-xs text-slate-500">결제 승인 상태</div>
            <div className="mt-1 flex flex-col gap-2">
              <select
                className="w-40 rounded border px-2 py-1 text-xs md:text-sm"
                value={approvalStatus}
                onChange={(e) => setApprovalStatus(e.target.value)}
              >
                {APPROVAL_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>

              {approvalStatus === "요청철회/거절" && (
                <div className="mt-1">
                  <div className="mb-1 text-xs text-slate-500">거절 사유</div>
                  <textarea
                    className="w-full rounded border px-2 py-1 text-xs"
                    rows={3}
                    placeholder="거절 사유를 입력해주세요. (알림톡에 포함됩니다.)"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-semibold text-slate-700">결제대기 예약</div>
                <div className="mt-1 text-[11px] text-slate-500">
                  한국시간(KST) 기준으로 승인대기 상태를 결제대기로 변경할 일시를 예약합니다.
                </div>
                <div className="jp-admin-schedule-actions mt-2">
                  <input
                    type="datetime-local"
                    className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs md:text-sm"
                    value={reserveAtInput}
                    onChange={(e) => setReserveAtInput(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={saveReserve}
                    disabled={savingReserve || !reserveAtInput}
                    className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {savingReserve ? "저장 중..." : "예약 저장"}
                  </button>
                  <button
                    type="button"
                    onClick={cancelReserve}
                    disabled={savingReserve || !reservedAt}
                    className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    예약 취소
                  </button>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                  현재 예약: {reservedAt ? formatDateTimeKST(reservedAt) : "없음"}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  즉시 상태를 결제대기로 저장하면 기존 예약은 서버 정책에 따라 자동 해제될 수 있습니다.
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500">결제명</div>
            <div className="text-sm">
              {item?.method || item?.title || item?.payment_name || "-"}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500">카테고리</div>
            <div className="text-sm">{formatPayType(item?.category)}</div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500">{splitSummary ? "총 요청금액" : "결제금액"}</div>
            <div className="text-sm">
              {typeof amount === "number" ? amount.toLocaleString() + "원" : "-"}
            </div>
          </div>
        </div>
      </section>

      {/* 카드결제 부가세 이중 환급 금지 안내 동의 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="jp-admin-agreement-heading"><h3 className="text-sm font-semibold text-slate-700">카드결제 부가세 이중 환급 금지 안내 동의</h3><span className={"jp-admin-agreement-status" + (vatAgreementLog?.agreed ? " is-agreed" : "")}>{vatAgreementLog ? (vatAgreementLog.agreed ? "동의" : "미동의") : "기록 없음"}</span></div>
        <details className="jp-admin-agreement-details"><summary>동의 기록 자세히 보기</summary>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <div className="text-xs text-slate-500">동의 여부</div>
            <div className="text-sm">{vatAgreementLog ? (vatAgreementLog.agreed ? "동의" : "미동의") : "-"}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">동의 일시</div>
            <div className="text-sm">
              {vatAgreementLog?.agreed_at ? formatDateTimeKST(vatAgreementLog.agreed_at) : "-"}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">버전</div>
            <div className="text-sm">{vatAgreementLog?.notice_version || "-"}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">세션</div>
            <div className="truncate text-sm">{vatAgreementLog?.session_key || "-"}</div>
          </div>
        </div>
        {vatAgreementLog?.notice_text ? (
          <div className="mt-3 whitespace-pre-line rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs leading-5 text-slate-600">
            {vatAgreementLog.notice_text}
          </div>
        ) : null}
        </details>
      </section>

      {splitSummary && splitItems.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">분할결제 요약</h3>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">분할 건수</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{splitSummary.split_count}건</div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">총 요청금액</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{splitSummary.amount_sum.toLocaleString()}원</div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">총 수수료</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{splitSummary.fee_sum.toLocaleString()}원</div>
            </div>
            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
              <div className="text-xs text-slate-500">총 결제금액</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{splitSummary.total_sum.toLocaleString()}원</div>
            </div>
          </div>

          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">분할</th>
                  <th className="px-3 py-2 text-left font-medium">상태</th>
                  <th className="px-3 py-2 text-right font-medium">요청금액</th>
                  <th className="px-3 py-2 text-right font-medium">총 결제금액</th>
                </tr>
              </thead>
              <tbody>
                {splitItems.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-700">{row.split_label}</td>
                    <td className="px-3 py-2 text-slate-700">{row.approval_status || row.status || "-"}</td>
                    <td className="px-3 py-2 text-right text-slate-900">{row.amount.toLocaleString()}원</td>
                    <td className="px-3 py-2 text-right text-slate-900">{row.total_amount.toLocaleString()}원</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* 신청인 정보 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">신청인 정보</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <div className="text-xs text-slate-500">신청인</div>
            <div className="text-sm">{item?.applicant_name || "-"}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">연락처</div>
            <div className="text-sm">{item?.applicant_phone || "-"}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">이메일</div>
            <div className="text-sm">{item?.email || "-"}</div>
          </div>
        </div>
      </section>

      {/* ✅ 등기부등본 발급용 주소 (임대료/월세일 때만 표시) */}
      {showRegistrySection && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">
            등기부등본 발급용 주소
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <div className="text-xs text-slate-500">주소</div>
              <div className="text-sm">{regAddr1 || "-"}</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-500">상세 주소</div>
              <div className="text-sm">{regAddr2 || "-"}</div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-slate-500">우편번호</div>
              <div className="text-sm">{regZone || "-"}</div>
            </div>
          </div>
        </section>
      )}

      {/* ✅ 기타내용 (신청인 메모) */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">
          기타내용 (신청인 메모)
        </h3>
        <div className="min-h-[60px] whitespace-pre-line rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-800">
          {item?.extra_note && item.extra_note.trim().length > 0
            ? item.extra_note
            : "신청인이 별도 메모를 남기지 않았습니다."}
        </div>
      </section>

      {/* 거래 상대 정보 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">거래 상대 정보</h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="space-y-1">
            <div className="text-xs text-slate-500">거래처 예금주</div>
            <div className="text-sm">{counterpartyName}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">거래상대 은행명</div>
            <div className="text-sm">{counterpartyBankName}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">거래상대 계좌번호</div>
            <div className="text-sm">{counterpartyAccount}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">송금일자</div>
            <div className="text-sm">
              {item?.transfer_date
                ? new Date(item.transfer_date).toLocaleDateString()
                : "-"}
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-xs text-slate-500">송금자명 설정</div>
            <div className="text-sm">{remitterDisplay}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="space-y-1">
            <div className="text-xs text-slate-500">수수료</div>
            <div className="text-sm">
              {fee ? `${fee.toLocaleString()}원` : "-"}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">부가세</div>
            <div className="text-sm">
              {vat ? `${vat.toLocaleString()}원` : "-"}
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-slate-500">
              총 결제금액(원금+수수료+부가세)
            </div>
            <div className="text-sm">
              {total != null ? `${total.toLocaleString()}원` : "-"}
            </div>
          </div>
        </div>
      </section>

      {/* 첨부/필수 서류 */}
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">
          첨부/필수 서류
        </h3>

        {!files.length ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
            첨부된 파일이 없습니다.
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="jp-admin-upload-card flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{f.name}</div>

                  {(f.docType || f.status) && (
                    <div className="mt-0.5 text-[11px] text-slate-500">
                      {f.docType && <span>{f.docType}</span>}
                      {f.docType && f.status && <span> · </span>}
                      {f.status && <span>{f.status}</span>}
                    </div>
                  )}

                  {f.createdAt && (
                    <div className="mt-0.5 text-[10px] text-slate-400">
                      {new Date(f.createdAt).toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="ml-3 flex shrink-0 gap-2">
                  <a
                    href={f.previewUrl}
                    onClick={event => { event.preventDefault(); void openAuthenticatedUrl(f.previewUrl); }}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-8 items-center rounded-lg border border-slate-300 px-3 text-sm hover:bg-slate-50"
                  >
                    미리보기
                  </a>
                  <a
                    href={f.downloadUrl}
                    onClick={event => { event.preventDefault(); void openAuthenticatedUrl(f.downloadUrl); }}
                    className="inline-flex h-8 items-center rounded-lg border border-slate-300 px-3 text-sm hover:bg-slate-50"
                  >
                    다운로드
                  </a>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {loading && <div className="text-center text-slate-500">불러오는 중…</div>}
    </div>
  );
}
